/**
 * Colour grid values by column type, HeidiSQL-style (owner, 30.09.2026):
 * numbers, text, dates, JSON, binary, NULL… each in its own colour, which
 * reads well. Toggleable and configurable in Settings → Extensions → DB
 * Manager; stored as the `dbTypeColors` preference.
 *
 * Rendering: a `dbm-td--t-<category>` class per cell and the colours as CSS
 * custom properties on the grid root (one set per theme), so changing a
 * colour or the toggle restyles a virtualised grid without re-rendering rows.
 */

export const TYPE_CATEGORIES = [
  { id: 'int', label: 'Integer', sample: '4711' },
  { id: 'dec', label: 'Decimal', sample: '19.90' },
  { id: 'text', label: 'Text', sample: 'Ada Lovelace' },
  { id: 'date', label: 'Date / time', sample: '2026-10-01 08:30' },
  { id: 'bool', label: 'Boolean', sample: 'true' },
  { id: 'json', label: 'JSON', sample: '{"a":1}' },
  { id: 'binary', label: 'Binary', sample: '16 B' },
  { id: 'uuid', label: 'UUID', sample: '3f2c…9a1e' },
  { id: 'enum', label: 'Enum / set', sample: 'paid' },
  { id: 'spatial', label: 'Spatial', sample: 'POINT(1 2)' },
  { id: 'null', label: 'NULL', sample: 'NULL' },
];

// Defaults: HeidiSQL's families (navy numbers, green text, maroon dates,
// purple binary…) toned for both themes — AA contrast on #fff and on the dark
// grid (#17171a).
export const DEFAULT_TYPE_COLORS = {
  light: {
    int: '#1e3a8a', dec: '#1e40af', text: '#166534', date: '#9f1239', bool: '#9a3412', json: '#0e7490',
    binary: '#6b21a8', uuid: '#3f6212', enum: '#92400e', spatial: '#4d7c0f', null: '#71717a',
  },
  dark: {
    int: '#93c5fd', dec: '#a5b4fc', text: '#86efac', date: '#fda4af', bool: '#fdba74', json: '#67e8f9',
    binary: '#d8b4fe', uuid: '#bef264', enum: '#fcd34d', spatial: '#a3e635', null: '#71717a',
  },
};

const HEX = /^#[0-9a-f]{6}$/i;

/** The stored preference, cleaned: { enabled, light: {cat: #hex}, dark: {…} } — overrides only. */
export function normalizeDbTypeColors(raw) {
  const out = { enabled: !(raw && raw.enabled === false), light: {}, dark: {} };
  for (const theme of ['light', 'dark']) {
    const src = raw && typeof raw[theme] === 'object' && raw[theme] ? raw[theme] : {};
    for (const { id } of TYPE_CATEGORIES) if (HEX.test(src[id] || '')) out[theme][id] = src[id].toLowerCase();
  }
  return out;
}

/** The effective colour of a category in a theme. */
export function typeColor(pref, theme, id) {
  return (pref?.[theme] || {})[id] || DEFAULT_TYPE_COLORS[theme][id];
}

/** `--dbm-type-int-light` … for the grid root's style. */
export function typeColorVars(pref) {
  const vars = {};
  for (const theme of ['light', 'dark']) {
    for (const { id } of TYPE_CATEGORIES) vars[`--dbm-type-${id}-${theme}`] = typeColor(pref, theme, id);
  }
  return vars;
}

/**
 * A raw column type (any engine's spelling) → category id, or null when it
 * is not one we colour. Order matters: boolean before integer (MySQL's
 * tinyint(1)), datetime/timestamp before the bare `time`, json before text.
 */
export function typeCategory(rawType) {
  if (!rawType) return null;
  const t = String(rawType).trim().toLowerCase();
  if (/^(bool|boolean)\b/.test(t) || /^tinyint\(1\)/.test(t) || /^bit\(1\)$/.test(t) || t === 'bit') return 'bool';
  if (/^(tinyint|smallint|mediumint|int|integer|bigint|int2|int4|int8|serial|bigserial|smallserial|year)\b/.test(t)) return 'int';
  if (/^(decimal|numeric|float|double|real|money|dec|fixed|float4|float8)\b/.test(t)) return 'dec';
  if (/^(timestamptz|timestamp|datetime2?|smalldatetime|datetimeoffset|date|timetz|time|interval)\b/.test(t)) return 'date';
  if (/^jsonb?\b/.test(t)) return 'json';
  if (/^uuid\b|^uniqueidentifier\b/.test(t)) return 'uuid';
  if (/^(enum|set)\b|^user-defined$/.test(t)) return 'enum';
  if (/^(bytea|blob|tinyblob|mediumblob|longblob|binary|varbinary|bit)\b/.test(t)) return 'binary';
  if (/^(geometry|geography|point|linestring|polygon|multipoint|multilinestring|multipolygon|geometrycollection|box|circle|path|line|lseg)\b/.test(t)) return 'spatial';
  if (/^(var\s?char2?|n?var\s?char|character varying|character|n?char|text|tinytext|mediumtext|longtext|clob|string|citext|name|xml|inet|cidr|macaddr)\b/.test(t)) return 'text';
  return null;
}

/** Query tabs carry no column types: fall back to the value's JS type. */
export function valueCategory(v) {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'number' || typeof v === 'bigint') return Number.isInteger(v) || typeof v === 'bigint' ? 'int' : 'dec';
  if (typeof v === 'boolean') return 'bool';
  if (typeof v === 'object') return v.__type === 'bytes' ? 'binary' : v.__undecoded ? null : 'json';
  return null; // a string of unknown type: leave it the normal colour
}
